import { Container, Singleton } from 'typescript-ioc/es5'
import { AppService } from './app'
import querystring from 'query-string'
import { axiosFetch, isAxiosCancellation } from '../config/axios-setup'
import aqe from '@pushly/aqe'
import { handleResponseErrorMessage } from '../_utils/response-error-utils'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { GetPromptGroupResponse } from '@pushly/models/lib/structs/prompts/get-prompt-group-response'
import { CreatePromptGroupRequest } from '@pushly/models/lib/structs/prompts/create-prompt-group-request'
import { UpdatePromptGroupRequest } from '@pushly/models/lib/structs/prompts/update-prompt-group-request'
import { GetAbstractPromptResponse } from '@pushly/models/lib/structs'

@Singleton
export class PromptService {
    private appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async fetchPromptGroupsByDomainId(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetPromptGroupResponse[]>> {
        const res: IApiResponse<GetPromptGroupResponse[]> = {
            ok: false,
            data: undefined,
            cancelled: false,
            meta: {},
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const qs = querystring.stringify(options.query || {})

            const req = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/v3/domains/${domainId}/prompt-groups?${qs}`,
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )

            const { data, ...meta } = req.data

            res.data = data.map((group) => GetPromptGroupResponse.parseJsonObject(group))
            res.meta = meta
            res.ok = true
        } catch (error) {
            res.cancelled = isAxiosCancellation(error)
            res.error = error
            console.error(error)
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async fetchPromptGroupById(
        domainId: number,
        promptGroupId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetPromptGroupResponse>> {
        const res: IApiResponse<GetPromptGroupResponse> = {
            ok: false,
            data: undefined,
            cancelled: false,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups/${promptGroupId}`,
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )

            res.ok = true
            res.data = GetPromptGroupResponse.from(req.data.data)
        } catch (error) {
            handleResponseErrorMessage(error)
            res.cancelled = isAxiosCancellation(error)
            res.error = error
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async createPromptGroup(
        domainId: number,
        promptGroup: CreatePromptGroupRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetPromptGroupResponse>> {
        const res: IApiResponse<GetPromptGroupResponse> = {
            ok: false,
            data: undefined,
            cancelled: false,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups`,
                    data: promptGroup.serialize(),
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )

            res.ok = true
            res.data = GetPromptGroupResponse.from(req.data.data)
        } catch (error) {
            handleResponseErrorMessage(error)
            res.cancelled = isAxiosCancellation(error)
            res.error = error
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async updatePromptGroupById(
        domainId: number,
        promptGroupId: number,
        updateRequest: UpdatePromptGroupRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetPromptGroupResponse>> {
        const res: IApiResponse<GetPromptGroupResponse> = {
            ok: false,
            data: undefined,
            cancelled: false,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups/${promptGroupId}`,
                    data: updateRequest.serialize(),
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )

            res.ok = true
            res.data = GetPromptGroupResponse.from(req.data.data)
        } catch (error) {
            handleResponseErrorMessage(error)
            res.cancelled = isAxiosCancellation(error)
            res.error = error
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async destroyPromptGroupById(
        domainId: number,
        promptGroupId: number,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<boolean | void> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            await axiosFetch(
                'delete',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups/${promptGroupId}`,
                },
                cancellationKey,
            )

            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
            return true
        } catch (error) {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
            throw error
        }
    }

    public async updatePromptGroupPriorityMap(
        domainId: number,
        priorityMap: any[],
        cancellationKey?: string,
    ): Promise<any | void> {
        try {
            const req = await axiosFetch(
                'post',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups/priorities`,
                    data: priorityMap,
                },
                cancellationKey,
            )
            const { data: map, ...data } = req.data

            return map
        } catch (error) {}
    }

    public async enablePromptGroupById(
        domainId: number,
        promptGroupId: number,
        cancellationKey?: string,
    ): Promise<GetPromptGroupResponse | void> {
        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups/${promptGroupId}`,
                    data: { isActive: true },
                },
                cancellationKey,
            )
            const { data: prompt, ...data } = req.data

            return GetPromptGroupResponse.parseJsonObject(prompt)
        } catch (error) {}
    }

    public async disablePromptGroupById(
        domainId: number,
        promptGroupId: number,
        cancellationKey?: string,
    ): Promise<GetPromptGroupResponse | void> {
        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/prompt-groups/${promptGroupId}`,
                    data: { isActive: false },
                },
                cancellationKey,
            )
            const { data: prompt, ...data } = req.data

            return GetPromptGroupResponse.parseJsonObject(prompt)
        } catch (error) {}
    }

    public async promoteTestPrompt(
        group: GetPromptGroupResponse,
        prompt: GetAbstractPromptResponse,
        options: IApiCallOptions = {},
    ): Promise<boolean | void> {
        try {
            await axiosFetch(
                'post',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${group.domainId}/prompt-groups/${group.id}/promote/${prompt.id}`,
                },
                options.cancellationKey,
            )

            return true
        } catch (error) {
            throw error
        }
    }
}
